import cloneDeep from 'lodash/cloneDeep';

import {
    DEFAULT_LONG_TEXT_MAX_LENGTH,
    DEFAULT_SHORT_TEXT_MAX_LENGTH,
} from '@webapp/account/src/resources/questions/fillers';
import {
    DateFormat,
    FileUploadType,
    ResidenceType,
    smileTypes,
} from '@webapp/account/src/resources/questions/questions';

import { SmilePackName } from '@webapp/common/components/smile-image';
import type { LogicCondition } from '@webapp/common/entities/logic';
import type { QuestionCol } from '@webapp/common/entities/question-col';
import { initialCol } from '@webapp/common/entities/question-col';
import type { QuestionRow } from '@webapp/common/entities/question-row';
import { initialRow } from '@webapp/common/entities/question-row';
import { DateTimeType, TimeFormat } from '@webapp/common/lib/date';
import { BoolAnswerText, getColor } from '@webapp/common/lib/ui';
import { initialQuestion } from '@webapp/common/resources/entities/question';
import type { QuestionParams } from '@webapp/common/resources/question';
import { AnswerType, DropdownType, QuestionType, TYPES_NAMES } from '@webapp/common/resources/survey';

export interface FillerQuestion {
    name: string;
    type: QuestionType;
    rows: Array<QuestionRow>;
    cols: Array<QuestionCol>;
    items?: AnyObject;
    params: QuestionParams;
    conditions: Array<LogicCondition>;
}

export const ROW_COUNTRY = {
    value: ResidenceType.COUNTRY,
    order: 1,
};

export const QuestionsWithAnswersInMultiCreation = new Set([
    QuestionType.TEST_FEW_OF_LIST,
    QuestionType.TEST_ONE_OF_LIST,
    QuestionType.GROUP_FREE_ANSWERS,
    QuestionType.SEM_DIFF,
    QuestionType.MATRIX_TEXT_ANSWER,
    QuestionType.MATRIX_FEW_ANSWERS,
    QuestionType.MATRIX_SINGLE_ANSWER,
    QuestionType.RANK,
    QuestionType.TEST_RANK,
    QuestionType.DISTRIBUTE_SCALE,
    QuestionType.SMILE,
    QuestionType.MATRIX_RATING,
    QuestionType.CSI,
    QuestionType.KANO,
    QuestionType.STAR,
    QuestionType.SCALE,
    QuestionType.MATRIX_DROPDOWN_LIST,
    QuestionType.ONE_OF_LIST,
    QuestionType.FEW_OF_LIST,
    QuestionType.DROPDOWN_LIST,
]);

export const MultiCreateFillers: { [key: string]: (title: string, answers: Array<string>) => FillerQuestion } = {
    [QuestionType.TEST_FEW_OF_LIST]: (title: string, answers: Array<string>) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.TEST_FEW_OF_LIST,
        rows: answers.map((value, idx) => ({
            ...cloneDeep(initialRow),
            value,
            id: idx + 1,
            order: idx + 1,
            value3: 0,
            fileId: null,
            filePath: null,
        })),
        cols: [],
        params: { ...cloneDeep(initialQuestion.params), totalScore: true, testScore: 1 },
        conditions: [],
    }),
    [QuestionType.TEST_ONE_OF_LIST]: (title: string, answers: Array<string>) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.TEST_ONE_OF_LIST,
        rows: answers.map((value, idx) => ({
            ...cloneDeep(initialRow),
            value,
            id: idx + 1,
            order: idx + 1,
            value3: 0,
            fileId: null,
            filePath: null,
        })),
        cols: [],
        params: cloneDeep(initialQuestion.params),
        conditions: [],
    }),
    [QuestionType.TEST_TEXT]: (title) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.TEST_TEXT,
        rows: [1].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
            value3: 0,
            fileId: null,
            filePath: null,
        })),
        cols: [],
        params: { ...cloneDeep(initialQuestion.params), testScore: 1 },
        conditions: [],
    }),
    [QuestionType.TEXT_BLOCK]: (title) => ({
        ...cloneDeep(initialQuestion),
        name: TYPES_NAMES[QuestionType.TEXT_BLOCK],
        type: QuestionType.TEXT_BLOCK,
        rows: [],
        cols: [],
        params: {
            ...cloneDeep(initialQuestion.params),
            textBlock: title,
            name: false,
            help: null,
            timer: null,
            comment: null,
            required: null,
        },
        conditions: [],
    }),
    [QuestionType.AGREEMENT]: () => ({
        ...cloneDeep(initialQuestion),
        type: QuestionType.AGREEMENT,
        rows: [1, 2].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
            value: [BoolAnswerText.TRUE, BoolAnswerText.FALSE][id - 1],
        })),
        cols: [],
        params: {
            ...cloneDeep(initialQuestion.params),
            name: false,
            timer: null,
            comment: null,
            agreementLeftLabel: 'Согласен с политикой обработки',
            agreementRightLabel: '',
            agreementLink: 'персональных данных',
            agreementText: '',
        },
        conditions: [],
    }),
    [QuestionType.PHONE]: (title) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.PHONE,
        rows: [1, 2].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
            value: ['моб.', 'дом.'][id - 1],
            fileId: null,
            filePath: null,
        })),
        cols: [],
        params: {
            ...cloneDeep(initialQuestion.params),
            comment: null,
            phoneFormatCountry: '+7',
            phoneFormatNumber: '(495) 123-45-67',
        },
        conditions: [],
    }),
    [QuestionType.DATETIME]: (title) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.DATETIME,
        rows: [1].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
            fileId: null,
            filePath: null,
        })),
        cols: [],
        params: {
            ...cloneDeep(initialQuestion.params),
            comment: null,
            dateFormat: DateFormat[0],
            dateType: DateTimeType.DATE_TIME,
            timeFormat: TimeFormat.TWENTY_FOUR,
        },
        conditions: [],
    }),
    [QuestionType.EMAIL]: (title) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.EMAIL,
        rows: [1].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
            fileId: null,
            filePath: null,
        })),
        cols: [],
        params: {
            ...cloneDeep(initialQuestion.params),
            comment: null,
        },
        conditions: [],
    }),
    [QuestionType.NAME]: (title) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.NAME,
        rows: [1].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
            value: '',
            fileId: null,
            filePath: null,
        })),
        cols: [1, 2, 3].map((id) => ({
            ...cloneDeep(initialCol),
            id,
            order: id,
            name: ['Фамилия', 'Имя', 'Отчество'][id - 1],
            answers: [],
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            comment: null,
        },
        conditions: [],
    }),
    [QuestionType.RESIDENCE]: (title) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.RESIDENCE,
        rows: [
            {
                ...cloneDeep(initialRow),
                ...ROW_COUNTRY,
            },
        ],
        cols: [],
        params: {
            ...cloneDeep(initialQuestion.params),
            residence: ResidenceType.COUNTRY,
        },
        conditions: [],
    }),
    [QuestionType.NESTED_LIST]: (title, answers) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.NESTED_LIST,
        rows: answers.map((answer) => ({
            ...cloneDeep(initialRow),
            value: answer,
            value3: 1,
            order: 1,
        })),
        cols: [],
        params: cloneDeep(initialQuestion.params),
        conditions: [],
    }),
    [QuestionType.FILE_UPLOAD]: (title) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.FILE_UPLOAD,
        rows: [],
        cols: [],
        params: {
            ...cloneDeep(initialQuestion.params),
            fileAmount: 1,
            uploadButtonName: 'Загрузить файл',
            fileType: FileUploadType.ANY,
            fileLimitExt: [],
        },
        conditions: [],
    }),
    [QuestionType.GROUP_FREE_ANSWERS]: (title, answers) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.GROUP_FREE_ANSWERS,
        rows: answers.map((value, idx) => ({
            ...cloneDeep(initialRow),
            value,
            id: idx + 1,
            order: idx + 1,
            fileId: null,
            filePath: null,
        })),
        cols: [],
        params: cloneDeep(initialQuestion.params),
        conditions: [],
    }),
    [QuestionType.NUMBER]: (title) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.NUMBER,
        rows: [],
        cols: [],
        params: cloneDeep(initialQuestion.params),
        conditions: [],
    }),
    [QuestionType.LONG_TEXT]: (title) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.LONG_TEXT,
        rows: [],
        cols: [],
        params: {
            ...cloneDeep(initialQuestion.params),
            comment: null,
            minLength: 0,
            maxLength: DEFAULT_LONG_TEXT_MAX_LENGTH,
        },
        conditions: [],
    }),
    [QuestionType.SHORT_TEXT]: (title) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.SHORT_TEXT,
        rows: [],
        cols: [],
        params: {
            ...cloneDeep(initialQuestion.params),
            minLength: 0,
            maxLength: DEFAULT_SHORT_TEXT_MAX_LENGTH,
            comment: null,
        },
        conditions: [],
    }),
    [QuestionType.SEM_DIFF]: (title, answers) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.SEM_DIFF,
        rows: [1].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
            fileId: null,
            filePath: null,
        })),
        cols: answers.map((value, idx) => ({
            ...cloneDeep(initialCol),
            value,
            id: idx + 1,
            order: idx + 1,
            name: `${idx + 1 - 3}`,
            answers: [],
            type: AnswerType.RADIO,
        })),
        params: cloneDeep(initialQuestion.params),
        conditions: [],
    }),
    [QuestionType.MATRIX_TEXT_ANSWER]: (title, answers) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.MATRIX_TEXT_ANSWER,
        rows: answers.map((value, idx) => ({
            ...cloneDeep(initialRow),
            value,
            id: idx + 1,
            order: idx + 1,
            fileId: null,
            filePath: null,
        })),
        cols: [1, 2, 3, 4, 5].map((id) => ({
            ...cloneDeep(initialCol),
            id,
            order: id,
            name: '',
            answers: [],
            type: AnswerType.TEXT,
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            answerType: 'any',
        },
        conditions: [],
    }),
    [QuestionType.MATRIX_FEW_ANSWERS]: (title, answers) => ({
        ...cloneDeep(initialQuestion),
        type: QuestionType.MATRIX_FEW_ANSWERS,
        name: title,
        rows: answers.map((value, idx) => ({
            ...cloneDeep(initialRow),
            value,
            id: idx + 1,
            order: idx + 1,
            fileId: null,
            filePath: null,
        })),
        cols: [1, 2, 3, 4, 5].map((id) => ({
            ...cloneDeep(initialCol),
            id,
            order: id,
            name: '',
            answers: [],
            type: AnswerType.CHECKBOX,
        })),
        params: cloneDeep(initialQuestion.params),
        conditions: [],
    }),
    [QuestionType.MATRIX_SINGLE_ANSWER]: (title, answers) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.MATRIX_SINGLE_ANSWER,
        rows: answers.map((value, idx) => ({
            ...cloneDeep(initialRow),
            value,
            id: idx + 1,
            order: idx + 1,
            fileId: null,
            filePath: null,
        })),
        cols: [1, 2, 3, 4, 5].map((id) => ({
            ...cloneDeep(initialCol),
            id,
            order: id,
            name: '',
            answers: [],
            type: AnswerType.RADIO,
        })),
        params: cloneDeep(initialQuestion.params),
        conditions: [],
    }),
    [QuestionType.RANK]: (title, answers) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.RANK,
        rows: answers.map((value, idx) => ({
            ...cloneDeep(initialRow),
            value,
            id: idx + 1,
            order: idx + 1,
            value3: '',
            fileId: null,
            filePath: null,
        })),
        cols: [],
        params: cloneDeep(initialQuestion.params),
        conditions: [],
    }),
    [QuestionType.TEST_RANK]: (title, answers) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.TEST_RANK,
        rows: answers.map((value, idx) => ({
            ...cloneDeep(initialRow),
            value,
            id: idx + 1,
            order: idx + 1,
            value3: '',
            fileId: null,
            filePath: null,
        })),
        cols: [],
        params: { ...cloneDeep(initialQuestion.params), randomOrder: true, testScore: 1 },
        conditions: [],
    }),
    [QuestionType.NPS]: (title) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.NPS,
        rows: [],
        cols: [],
        params: {
            ...cloneDeep(initialQuestion.params),
            size: 1,
            npsLeftLabel: 'Маловероятно',
            npsRightLabel: 'С большой вероятностью',
            npsColors: [getColor('blue-nps')],
        },
        conditions: [],
    }),
    [QuestionType.DISTRIBUTE_SCALE]: (title, answers) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.DISTRIBUTE_SCALE,
        rows: answers.map((value, idx) => ({
            ...cloneDeep(initialRow),
            value,
            id: idx + 1,
            order: idx + 1,
            fileId: null,
            filePath: null,
        })),
        cols: [],
        params: {
            ...cloneDeep(initialQuestion.params),
            distributeSurplus: true,
            distributeLabel: 'Распределить',
            distributeScore: 100,
            distributeUnit: 'баллов',
        },
        conditions: [],
    }),
    [QuestionType.SMILE]: (title, answers) => {
        const smileType = (() => {
            if (answers.length === 2) {
                return '2-smile';
            }
            if (answers.length === 3) {
                return '3-smile';
            }
            return '5-smile';
        })();
        const smiles = (smileTypes.find((st) => st.value === smileType) ?? smileTypes[0])?.smiles;
        return {
            ...cloneDeep(initialQuestion),
            name: title,
            type: QuestionType.SMILE,
            rows: smiles.map((smile, key) => {
                return {
                    ...cloneDeep(initialRow),
                    id: key,
                    value: smile,
                    order: key,
                    fileId: null,
                    filePath: null,
                    width: 0,
                    height: 0,
                    mimeType: null,
                };
            }),
            cols: [],
            params: {
                ...cloneDeep(initialQuestion.params),
                size: 1,
                smilePack: SmilePackName.PACK1,
                smileType,
            },
            conditions: [],
        };
    },
    [QuestionType.MATRIX_RATING]: (title, answers) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.MATRIX_RATING,
        rows: answers.map((value, idx) => ({
            ...cloneDeep(initialRow),
            value,
            id: idx + 1,
            order: idx + 1,
            fileId: null,
            filePath: null,
        })),
        cols: [],
        params: {
            ...cloneDeep(initialQuestion.params),
            amount: 5,
            size: 1,
            color: getColor('blue-star'),
        },
        conditions: [],
    }),
    [QuestionType.CSI]: (title, answers) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.CSI,
        rows: answers.map((value, idx) => {
            return {
                ...cloneDeep(initialRow),
                value,
                id: idx + 1,
                order: idx + 1,
                fileId: null,
                filePath: null,
                value2: 'Оценка',
                value3: 'Важность данного критерия для вас',
                commentText: 'Текст комментария',
            };
        }),
        cols: [1, 2].map((id) => ({
            ...cloneDeep(initialCol),
            id,
            order: id,
            name: '',
            answers: [],
            type: AnswerType.TEXT,
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            amount: 10,
            size: 1,
            color: getColor('blue-star'),
            matrixType: 'numbers',
        },
        conditions: [],
    }),
    [QuestionType.KANO]: (title, answers) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.KANO,
        rows: answers.map((value, idx) => {
            return {
                ...cloneDeep(initialRow),
                value,
                id: idx + 1,
                order: idx + 1,
                fileId: null,
                filePath: null,
                value2: '<p>Как вы отнесётесь, если функция есть?</p>',
                value3: '<p>Как вы отнесётесь, если функции нет?</p>',
                commentText: 'Текст комментария',
            };
        }),
        cols: [],
        params: {
            ...cloneDeep(initialQuestion.params),
            comment: false,
            cantRate: false,
        },
        conditions: [],
    }),
    [QuestionType.STAR]: (title, answers) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.STAR,
        rows: [],
        cols: [],
        params: {
            ...cloneDeep(initialQuestion.params),
            color: getColor('blue-star'),
            size: 2,
            amount: Math.max(Math.min(answers.length, 10), 3),
        },
        conditions: [],
    }),
    [QuestionType.SCALE]: (title, answers) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.SCALE,
        rows: [1].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
            fileId: null,
            filePath: null,
        })),
        cols: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((id) => ({
            ...cloneDeep(initialCol),
            id,
            order: id,
            numericValue: id,
            name: '',
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            size: 1,
            npsLeftLabel: 'Плохо',
            npsRightLabel: 'Отлично',
            scaleEndDia: answers.length,
        },
        conditions: [],
    }),
    [QuestionType.MATRIX_DROPDOWN_LIST]: (title, answers) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.MATRIX_DROPDOWN_LIST,
        rows: answers.map((value, idx) => ({
            ...cloneDeep(initialRow),
            value,
            id: idx + 1,
            order: idx + 1,
            fileId: null,
            filePath: null,
        })),
        cols: [1, 2, 3, 4, 5].map((id) => ({
            ...cloneDeep(initialCol),
            id,
            order: id,
            name: '',
            type: AnswerType.SELECT,
            answers: [],
        })),
        params: {
            ...cloneDeep(initialQuestion.params),
            dropdownListType: DropdownType.DIFFERENT,
            answers: [],
        },
        conditions: [],
    }),
    [QuestionType.ONE_OF_LIST]: (title, answers) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.ONE_OF_LIST,
        rows: answers.map((value, idx) => ({
            ...cloneDeep(initialRow),
            value,
            id: idx + 1,
            order: idx + 1,
            value3: '',
            fileId: null,
            filePath: null,
        })),
        cols: [],
        params: {
            ...cloneDeep(initialQuestion.params),
            randomOrder: false,
        },
        conditions: [],
    }),
    [QuestionType.FEW_OF_LIST]: (title, answers) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.FEW_OF_LIST,
        rows: answers.map((value, idx) => ({
            ...cloneDeep(initialRow),
            value,
            id: idx + 1,
            order: idx + 1,
            value3: '',
            fileId: null,
            filePath: null,
        })),
        cols: [],
        params: cloneDeep(initialQuestion.params),
        conditions: [],
    }),
    [QuestionType.SELECT_ONE_IMAGE]: (title) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.SELECT_ONE_IMAGE,
        rows: [1, 2, 3].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
            fileId: null,
            filePath: null,
        })),
        cols: [],
        params: {
            ...cloneDeep(initialQuestion.params),
            imageColumns: 1,
            size: 100,
            imageTextPosition: 'above',
        },
        conditions: [],
    }),
    [QuestionType.SELECT_FEW_IMAGE]: (title) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.SELECT_FEW_IMAGE,
        rows: [1, 2, 3].map((id) => ({
            ...cloneDeep(initialRow),
            id,
            order: id,
            fileId: null,
            filePath: null,
        })),
        cols: [],
        params: {
            ...cloneDeep(initialQuestion.params),
            imageColumns: 1,
            size: 100,
            imageTextPosition: 'above',
        },
        conditions: [],
    }),
    [QuestionType.DROPDOWN_LIST]: (title, answers) => ({
        ...cloneDeep(initialQuestion),
        name: title,
        type: QuestionType.DROPDOWN_LIST,
        rows: answers.map((value, idx) => ({
            ...cloneDeep(initialRow),
            value,
            id: idx + 1,
            order: idx + 1,
            value3: '',
            fileId: null,
            filePath: null,
        })),
        cols: [],
        params: cloneDeep(initialQuestion.params),
        conditions: [],
    }),
};
